import React, { useState } from 'react';
import { X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { toast } from 'sonner';

const QuickInquiryModal = ({ isOpen, onClose, product = null }) => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    country: '',
    medication: product?.name || '',
    message: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Quick inquiry submitted:', formData);
    toast.success('Inquiry submitted! Our expert will contact you within 24 hours.');
    setFormData({
      fullName: '',
      email: '',
      phone: '',
      country: '',
      medication: '',
      message: ''
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-gray-800 to-gray-900 text-white p-6 rounded-t-2xl flex items-center justify-between">
          <div>
            <h3 className="text-2xl font-bold">Quick Inquiry</h3>
            <p className="text-gray-300 text-sm mt-1">Get expert assistance within 24 hours</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Product Info (if available) */}
        {product && (
          <div className="p-6 bg-gray-50 border-b border-gray-200">
            <p className="text-sm text-gray-600 mb-1">Inquiring about:</p>
            <p className="text-lg font-bold text-gray-900">{product.name}</p>
            <p className="text-sm text-gray-600">{product.indication}</p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="fullName">Full Name *</Label>
              <Input
                id="fullName"
                name="fullName"
                type="text"
                placeholder="John Doe"
                value={formData.fullName}
                onChange={handleChange}
                required
                className="h-12"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email Address *</Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="john@example.com"
                value={formData.email}
                onChange={handleChange}
                required
                className="h-12"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number *</Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                placeholder="+1 234 567 8900"
                value={formData.phone}
                onChange={handleChange}
                required
                className="h-12"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="country">Country *</Label>
              <Input
                id="country"
                name="country"
                type="text"
                placeholder="United States"
                value={formData.country}
                onChange={handleChange}
                required
                className="h-12"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="medication">Medication Name *</Label>
            <Input
              id="medication"
              name="medication"
              type="text"
              placeholder="Enter medication name"
              value={formData.medication}
              onChange={handleChange}
              required
              className="h-12"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="message">Message / Additional Requirements</Label>
            <Textarea
              id="message"
              name="message"
              placeholder="Please provide any additional details like dosage, quantity, prescription availability, etc."
              value={formData.message}
              onChange={handleChange}
              rows={4}
              className="resize-none"
            />
          </div>

          <div className="flex gap-4">
            <Button
              type="submit"
              className="flex-1 bg-gray-800 hover:bg-gray-900 text-white py-3 text-lg rounded-lg font-semibold"
            >
              Submit Inquiry
            </Button>
            <Button
              type="button"
              onClick={onClose}
              variant="outline"
              className="px-8 py-3 text-lg rounded-lg border-2 border-gray-300"
            >
              Cancel
            </Button>
          </div>

          <p className="text-xs text-gray-600 text-center">
            Response time: Within 24 hours | Available: Monday - Saturday, 9 AM - 6 PM IST
          </p>
        </form>
      </div>
    </div>
  );
};

export default QuickInquiryModal;